import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, { SingleValue, StylesConfig } from "react-select";
import Skeleton from "react-loading-skeleton";
import { useGetCartQuery } from "../../api/cartApi";
import { useSelectShippingRate } from "../../hooks/useSelectShippingRate";
import { useUpdateCustomer } from "../../hooks/useUpdateCustomer";
import { useMediaQuery } from "../../../../../common/hooks/hooks";
import Icon from "../../../../../common/components/Icon/Icon";

// --- Type for react-select options ---
interface StateOption {
  value: string;
  label: string;
}

// Add the new prop to the interface
interface CartShippingCalculatorProps {
  namePrefix: string;
  isModalContext?: boolean;
}

const CartShippingCalculatorSkeleton: React.FC = () => (
  <div className="cart-shipping-calculator">
    <h3 className="cart-shipping-calculator__title">
      <Skeleton width={150} />
    </h3>
    <div className="cart-shipping-calculator__rates">
      <Skeleton height={40} count={2} style={{ marginBottom: "0.5rem" }} />
    </div>
    <div className="cart-shipping-calculator__address">
      <Skeleton width={200} />
    </div>
  </div>
);

const CartShippingCalculator: React.FC<CartShippingCalculatorProps> = ({
  namePrefix,
  isModalContext = false,
}) => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const { data: cart, isLoading: isCartLoading } = useGetCartQuery();
  const { handleSelectShippingRate, isSelectingRate } = useSelectShippingRate();
  const { handleUpdateCustomer, isUpdatingCustomer } = useUpdateCustomer();

  // 1. Use the media query hook to detect screen size.
  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  const isLoading = isSelectingRate || isUpdatingCustomer || isCartLoading;

  // 2. Create a dynamic styles object for react-select.
  const selectStyles: StylesConfig<StateOption, false> = {
    // This part handles the portaling for the modal context.
    ...(isModalContext && {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    // This new part handles the responsive height of the dropdown menu.
    menuList: (base) => ({
      ...base,
      maxHeight: isSmallScreen ? "132px" : "264px", // Approx. 3 items on mobile, 6 on desktop
    }),
  };

  // 3. Prepare State Data
  // Use current country from cart or default to TR if missing (or first available)
  const currentCountry = cart?.shippingAddress?.country || "TR";
  const countryStates = window.checkoutFlowVariables?.countryStates || {};
  const currentStates = countryStates[currentCountry] || {};

  const stateOptions: StateOption[] = useMemo(() => {
    return Object.entries(currentStates).map(([code, name]) => ({
      value: code,
      label: name,
    }));
  }, [currentStates]);

  if (isCartLoading || !areTranslationsReady) {
    return <CartShippingCalculatorSkeleton />;
  }

  if (!cart || !cart.needsShipping || !cart.hasCalculatedShipping) {
    return null;
  }

  const handleRateSelection = (rateId: string) => {
    handleSelectShippingRate({ package_id: 0, rate_id: rateId });
  };

  const handleStateChange = (selectedOption: SingleValue<StateOption>) => {
    if (selectedOption) {
      handleUpdateCustomer({
        shipping_address: { state: selectedOption.value },
      });
    }
    setIsEditing(false);
  };

  const { state } = cart.shippingAddress;
  // We only consider address details present if the State is selected
  const hasAddressDetails = !!state;

  // Direct Lookup: If state code exists in map, use name. Else use code.
  const stateName = state ? currentStates[state] || state : "";

  const selectedStateOption = stateOptions.find((opt) => opt.value === state);

  return (
    <div className="cart-shipping-calculator">
      <h3 className="cart-shipping-calculator__title">
        {t("cartShippingCalculator.title", "Shipping")}
      </h3>

      {cart.shippingRates.length > 0 ? (
        <>
          <ul className="cart-shipping-calculator__rates">
            {cart.shippingRates.map((rate) => (
              <li key={rate.rateId} className="cart-shipping-calculator__rate">
                <label>
                  <input
                    type="radio"
                    // Use the unique name here
                    name={`${namePrefix}_shipping_method`}
                    checked={rate.selected}
                    onChange={() => handleRateSelection(rate.rateId)}
                    disabled={isLoading}
                  />
                  <span className="rate-name">{rate.name}</span>
                  <span className="rate-price">{rate.price}</span>
                </label>
              </li>
            ))}
          </ul>
          {hasAddressDetails &&
            (isEditing ? (
              <div className="cart-shipping-calculator__edit">
                <Select
                  instanceId={`${namePrefix}-shipping-state-select`}
                  options={stateOptions}
                  value={selectedStateOption}
                  onChange={handleStateChange}
                  isDisabled={isLoading}
                  className="cart-shipping__select-container"
                  classNamePrefix="cart-shipping-select"
                  aria-label={t(
                    "cartShippingCalculator.changeStateLabel",
                    "Change State/Province"
                  )}
                  menuIsOpen={true}
                  onBlur={() => setIsEditing(false)}
                  autoFocus
                  // Apply the dynamic styles and conditional portal prop.
                  styles={selectStyles}
                  menuPortalTarget={isModalContext ? document.body : undefined}
                />
              </div>
            ) : (
              <p className="cart-shipping-calculator__address">
                {t("cartShippingCalculator.shippingTo", "Shipping to ")}
                <strong>{stateName}</strong>.
                <button
                  type="button"
                  className="cart-shipping-calculator__tooltip-trigger"
                  aria-label={t(
                    "cartShippingCalculator.tooltipLabel",
                    "More information about shipping address"
                  )}
                  data-tooltip={t(
                    "cartShippingCalculator.tooltipText",
                    "Select a shipping address to see a more accurate cart total. You can enter detailed address information at the checkout stage."
                  )}
                >
                  <Icon iconName="info-outlined" />
                </button>
                <button
                  className="cart-shipping-calculator__change-btn"
                  onClick={() => setIsEditing(true)}
                >
                  {t("cartShippingCalculator.changeButton", "Change")}
                </button>
              </p>
            ))}
        </>
      ) : (
        <p>
          {t(
            "cartShippingCalculator.noRates",
            "No shipping options were found for your address."
          )}
        </p>
      )}
    </div>
  );
};

export default CartShippingCalculator;
